import { useState, useMemo } from 'react';
import { useExpenses } from '../contexts/ExpenseContext';
import { CATEGORIES, MONTHS } from '../types';
import {
  Calculator,
  DollarSign,
  TrendingUp,
  TrendingDown,
  Truck,
  Fuel,
  Utensils,
  Phone,
  Shield,
  FileText,
  AlertTriangle,
  CheckCircle2,
  Download,
  ChevronDown,
  ChevronUp,
  Calendar,
  MapPin,
  Loader2,
  Receipt,
  Building,
  Wrench,
  Car
} from 'lucide-react';

// Per diem rates by fiscal year (Oct 1 - Sep 30)
const PER_DIEM_RATES: Record<number, number> = {
  2019: 66,
  2020: 66,
  2021: 66,
  2022: 69,
  2023: 69,
  2024: 69,
  2025: 80, // Updated rate for FY2025
};

// DOT deduction percentage for transportation workers
const DOT_DEDUCTION_PERCENT = 0.80;

// Categories that count as deductible expenses
const DEDUCTIBLE_CATEGORIES: Record<string, { label: string; icon: React.ReactNode; schedule: string }> = {
  fuel: { label: 'Fuel & Oil', icon: <Fuel className="w-4 h-4" />, schedule: 'Schedule C Line 9' },
  travel: { label: 'Travel & Lodging', icon: <Car className="w-4 h-4" />, schedule: 'Schedule C Line 24a' },
  meals: { label: 'Meals (50%)', icon: <Utensils className="w-4 h-4" />, schedule: 'Schedule C Line 24b' },
  maintenance: { label: 'Repairs & Maintenance', icon: <Wrench className="w-4 h-4" />, schedule: 'Schedule C Line 21' },
  insurance: { label: 'Insurance', icon: <Shield className="w-4 h-4" />, schedule: 'Schedule C Line 15' },
  phone: { label: 'Phone/Internet (% Business)', icon: <Phone className="w-4 h-4" />, schedule: 'Schedule C Line 25' },
  tolls: { label: 'Tolls & Parking', icon: <Receipt className="w-4 h-4" />, schedule: 'Schedule C Line 27a' },
  permits: { label: 'Licenses & Permits', icon: <FileText className="w-4 h-4" />, schedule: 'Schedule C Line 23' },
  bol: { label: 'BOL & Office', icon: <FileText className="w-4 h-4" />, schedule: 'Schedule C Line 18' },
  miscellaneous: { label: 'Other Expenses', icon: <Receipt className="w-4 h-4" />, schedule: 'Schedule C Line 27a' },
};

interface YearSummary {
  year: number;
  grossIncome: number;
  settlementCount: number;
  totalMiles: number;
  otrDays: number;
  perDiemEligible: number;
  perDiemDeduction: number;
  expenses: Record<string, { amount: number; count: number }>;
  totalExpenses: number;
  netProfit: number;
  estimatedTax: number;
  monthsWithData: number[];
  missingMonths: number[];
  warnings: string[];
}

export default function TaxSummary() {
  const { expenses, isLoading } = useExpenses();
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    income: true,
    perdiem: true,
    expenses: true,
    summary: true
  });

  // Get available years from data
  const availableYears = useMemo(() => {
    const years = new Set<number>();
    const currentYear = new Date().getFullYear();
    
    // Add years from 2019 to current
    for (let y = 2019; y <= currentYear; y++) {
      years.add(y);
    }
    
    // Also add any years from data
    expenses.forEach(e => years.add(e.year));
    
    return Array.from(years).sort((a, b) => b - a);
  }, [expenses]);

  // Calculate year summary
  const yearSummary = useMemo((): YearSummary => {
    const yearExpenses = expenses.filter(e => e.year === selectedYear);
    
    // Income from settlements
    const settlements = yearExpenses.filter(e => e.category === 'settlements');
    const grossIncome = settlements.reduce((sum, e) => sum + (e.amount || 0), 0);
    const settlementCount = settlements.length;
    
    // Calculate total miles from settlements
    const totalMiles = settlements.reduce((sum, e) => {
      if (e.miles) return sum + e.miles;
      // Estimate miles if not stored (~$1.75/mile average)
      return sum + Math.round((e.amount || 0) / 1.75);
    }, 0);
    
    // Calculate OTR days from settlements
    // Each settlement represents a trip - estimate days based on miles
    const otrDays = settlements.reduce((sum, s) => {
      const miles = s.miles || Math.round((s.amount || 0) / 1.75);
      const days = Math.max(1, Math.ceil(miles / 500)); // ~500 miles/day average
      return sum + days;
    }, 0);
    
    // Per diem calculation
    const perDiemRate = PER_DIEM_RATES[selectedYear] || PER_DIEM_RATES[2025];
    const perDiemEligible = otrDays * perDiemRate;
    const perDiemDeduction = perDiemEligible * DOT_DEDUCTION_PERCENT;
    
    // Categorize expenses
    const expensesByCategory: Record<string, { amount: number; count: number }> = {};
    let totalExpenses = 0;
    
    Object.keys(DEDUCTIBLE_CATEGORIES).forEach(cat => {
      expensesByCategory[cat] = { amount: 0, count: 0 };
    });
    
    yearExpenses.forEach(e => {
      if (e.category === 'settlements') return; // Skip income
      
      const cat = DEDUCTIBLE_CATEGORIES[e.category] ? e.category : 'miscellaneous';
      if (!expensesByCategory[cat]) {
        expensesByCategory[cat] = { amount: 0, count: 0 };
      }
      
      let amount = e.amount || 0;
      
      // Meals are only 50% deductible (unless per diem is used)
      // We'll track full amount but note the limitation
      
      expensesByCategory[cat].amount += amount;
      expensesByCategory[cat].count++;
      totalExpenses += amount;
    });
    
    // Calculate net profit and estimated self-employment tax
    const netProfit = grossIncome - totalExpenses - perDiemDeduction;
    const selfEmploymentTax = netProfit > 0 ? netProfit * 0.9235 * 0.153 : 0;
    const estimatedIncomeTax = netProfit > 0 ? netProfit * 0.22 : 0; // Rough estimate
    const estimatedTax = selfEmploymentTax + estimatedIncomeTax;
    
    // Check which months have data
    const monthsWithData = Array.from(new Set(yearExpenses.map(e => e.month))).sort((a, b) => a - b);
    const missingMonths = Array.from({ length: 12 }, (_, i) => i + 1).filter(m => !monthsWithData.includes(m));
    
    // Generate warnings
    const warnings: string[] = [];
    
    if (settlementCount === 0) {
      warnings.push('No settlement/income records found for this year');
    }
    
    if (missingMonths.length > 6) {
      warnings.push(`Missing data for ${missingMonths.length} months - may be incomplete`);
    }
    
    if (otrDays > 365) {
      warnings.push('OTR days exceed 365 - review settlement dates');
    }
    
    if (totalMiles > 200000) {
      warnings.push('Total miles seem high - verify settlement data');
    }
    
    const fuelExpense = expensesByCategory['fuel']?.amount || 0;
    if (fuelExpense > 0 && grossIncome > 0 && fuelExpense / grossIncome > 0.40) {
      warnings.push('Fuel expenses exceed 40% of income - verify entries');
    }
    
    return {
      year: selectedYear,
      grossIncome,
      settlementCount,
      totalMiles,
      otrDays,
      perDiemEligible,
      perDiemDeduction,
      expenses: expensesByCategory,
      totalExpenses,
      netProfit,
      estimatedTax,
      monthsWithData,
      missingMonths,
      warnings
    };
  }, [expenses, selectedYear]);

  // Toggle section expansion
  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Export tax summary to text
  const exportSummary = () => {
    const lines = [
      `═══════════════════════════════════════════════`,
      `        TAX SUMMARY - ${selectedYear}`,
      `═══════════════════════════════════════════════`,
      ``,
      `GROSS INCOME`,
      `────────────────────────────────────────────────`,
      `  Total Settlements: $${yearSummary.grossIncome.toLocaleString()}`,
      `  Number of Trips: ${yearSummary.settlementCount}`,
      `  Total Miles: ${yearSummary.totalMiles.toLocaleString()}`,
      ``,
      `PER DIEM DEDUCTION (DOT Special Rate)`,
      `────────────────────────────────────────────────`,
      `  OTR Days: ${yearSummary.otrDays}`,
      `  Daily Rate: $${PER_DIEM_RATES[selectedYear] || PER_DIEM_RATES[2025]}`,
      `  Total Eligible: $${yearSummary.perDiemEligible.toLocaleString()}`,
      `  Deductible (80%): $${yearSummary.perDiemDeduction.toLocaleString()}`,
      ``,
      `BUSINESS EXPENSES`,
      `────────────────────────────────────────────────`,
    ];
    
    Object.entries(DEDUCTIBLE_CATEGORIES).forEach(([key, cat]) => {
      const data = yearSummary.expenses[key];
      if (data && data.amount > 0) {
        lines.push(`  ${cat.label}: $${data.amount.toLocaleString()} (${data.count} entries)`);
      }
    });
    
    lines.push(`────────────────────────────────────────────────`);
    lines.push(`  TOTAL EXPENSES: $${yearSummary.totalExpenses.toLocaleString()}`);
    lines.push(``);
    lines.push(`SUMMARY`);
    lines.push(`────────────────────────────────────────────────`);
    lines.push(`  Gross Income:        $${yearSummary.grossIncome.toLocaleString()}`);
    lines.push(`  - Business Expenses: $${yearSummary.totalExpenses.toLocaleString()}`);
    lines.push(`  - Per Diem (80%):    $${yearSummary.perDiemDeduction.toLocaleString()}`);
    lines.push(`────────────────────────────────────────────────`);
    lines.push(`  NET PROFIT:          $${yearSummary.netProfit.toLocaleString()}`);
    lines.push(``);
    lines.push(`ESTIMATED TAX LIABILITY`);
    lines.push(`────────────────────────────────────────────────`);
    lines.push(`  Self-Employment Tax: ~$${(yearSummary.netProfit > 0 ? yearSummary.netProfit * 0.9235 * 0.153 : 0).toLocaleString()}`);
    lines.push(`  Est. Income Tax:     ~$${(yearSummary.netProfit > 0 ? yearSummary.netProfit * 0.22 : 0).toLocaleString()}`);
    lines.push(`  TOTAL ESTIMATED:     ~$${yearSummary.estimatedTax.toLocaleString()}`);
    lines.push(``);
    
    if (yearSummary.warnings.length > 0) {
      lines.push(`⚠️ WARNINGS`);
      lines.push(`────────────────────────────────────────────────`);
      yearSummary.warnings.forEach(w => lines.push(`  • ${w}`));
      lines.push(``);
    }
    
    lines.push(`Data Coverage: ${yearSummary.monthsWithData.length}/12 months`);
    if (yearSummary.missingMonths.length > 0) {
      lines.push(`Missing: ${yearSummary.missingMonths.map(m => MONTHS[m-1].slice(0,3)).join(', ')}`);
    }
    lines.push(``);
    lines.push(`Generated: ${new Date().toLocaleDateString()}`);
    lines.push(`═══════════════════════════════════════════════`);
    
    const blob = new Blob([lines.join('\n')], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `tax_summary_${selectedYear}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-white" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <Calculator className="w-8 h-8 text-emerald-400" />
            Tax Summary
          </h1>
          <p className="text-white/60 mt-1">
            Annual deduction calculations for Schedule C
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
            className="px-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white text-lg font-medium focus:outline-none focus:ring-2 focus:ring-emerald-500"
          >
            {availableYears.map(year => (
              <option key={year} value={year} className="bg-slate-800">
                {year}
              </option>
            ))}
          </select>
          
          <button
            onClick={exportSummary}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-500/20 text-emerald-400 border border-emerald-500/50 rounded-xl hover:bg-emerald-500/30 transition-colors"
          >
            <Download className="w-5 h-5" />
            Export
          </button>
        </div>
      </div>

      {/* Warnings */}
      {yearSummary.warnings.length > 0 && (
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-2xl p-4">
          <div className="flex items-center gap-2 text-yellow-400 mb-2">
            <AlertTriangle className="w-5 h-5" />
            <span className="font-medium">Attention Required</span>
          </div>
          <ul className="space-y-1 text-yellow-200/80 text-sm">
            {yearSummary.warnings.map((w, i) => (
              <li key={i}>• {w}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Data Coverage */}
      <div className="bg-white/5 rounded-2xl p-4 border border-white/10">
        <div className="flex items-center justify-between mb-3">
          <span className="text-white/80 font-medium flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            {selectedYear} Data Coverage
          </span>
          <span className={`text-sm font-medium px-2 py-0.5 rounded ${
            yearSummary.monthsWithData.length >= 10 ? 'bg-emerald-500/20 text-emerald-400' :
            yearSummary.monthsWithData.length >= 6 ? 'bg-yellow-500/20 text-yellow-400' :
            'bg-red-500/20 text-red-400'
          }`}>
            {yearSummary.monthsWithData.length}/12 months
          </span>
        </div>
        <div className="grid grid-cols-12 gap-1">
          {MONTHS.map((month, idx) => {
            const hasData = yearSummary.monthsWithData.includes(idx + 1);
            return (
              <div
                key={month}
                className={`p-2 rounded-lg text-center text-xs ${
                  hasData
                    ? 'bg-emerald-500/30 text-emerald-300'
                    : 'bg-white/5 text-white/30'
                }`}
                title={`${month}: ${hasData ? 'Has data' : 'No data'}`}
              >
                {month.slice(0, 3)}
              </div>
            );
          })}
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-emerald-500/20 to-emerald-600/10 rounded-2xl p-5 border border-emerald-500/30">
          <div className="flex items-center gap-2 text-emerald-400 mb-1">
            <TrendingUp className="w-4 h-4" />
            <span className="text-xs font-medium">Gross Income</span>
          </div>
          <p className="text-2xl font-bold text-white">
            ${yearSummary.grossIncome.toLocaleString()}
          </p>
          <p className="text-xs text-emerald-400/70">{yearSummary.settlementCount} settlements</p>
        </div>
        
        <div className="bg-gradient-to-br from-red-500/20 to-red-600/10 rounded-2xl p-5 border border-red-500/30">
          <div className="flex items-center gap-2 text-red-400 mb-1">
            <TrendingDown className="w-4 h-4" />
            <span className="text-xs font-medium">Total Deductions</span>
          </div>
          <p className="text-2xl font-bold text-white">
            ${(yearSummary.totalExpenses + yearSummary.perDiemDeduction).toLocaleString()}
          </p>
          <p className="text-xs text-red-400/70">Expenses + Per Diem</p>
        </div>
        
        <div className="bg-gradient-to-br from-blue-500/20 to-blue-600/10 rounded-2xl p-5 border border-blue-500/30">
          <div className="flex items-center gap-2 text-blue-400 mb-1">
            <DollarSign className="w-4 h-4" />
            <span className="text-xs font-medium">Net Profit</span>
          </div>
          <p className={`text-2xl font-bold ${yearSummary.netProfit >= 0 ? 'text-white' : 'text-orange-400'}`}>
            ${yearSummary.netProfit.toLocaleString()}
          </p>
          <p className="text-xs text-blue-400/70">Taxable income</p>
        </div>
        
        <div className="bg-gradient-to-br from-purple-500/20 to-purple-600/10 rounded-2xl p-5 border border-purple-500/30">
          <div className="flex items-center gap-2 text-purple-400 mb-1">
            <Building className="w-4 h-4" />
            <span className="text-xs font-medium">Est. Tax Due</span>
          </div>
          <p className="text-2xl font-bold text-white">
            ${Math.round(yearSummary.estimatedTax).toLocaleString()}
          </p>
          <p className="text-xs text-purple-400/70">SE + Income tax</p>
        </div>
      </div>

      {/* Gross Income Section */}
      <div className="bg-white/5 rounded-2xl border border-white/10 overflow-hidden">
        <button
          onClick={() => toggleSection('income')}
          className="w-full flex items-center justify-between p-5 hover:bg-white/5 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-emerald-500/20 rounded-lg">
              <TrendingUp className="w-5 h-5 text-emerald-400" />
            </div>
            <div className="text-left">
              <h2 className="text-lg font-semibold text-white">Gross Income</h2>
              <p className="text-sm text-white/60">Schedule C Line 1</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-2xl font-bold text-emerald-400">
              ${yearSummary.grossIncome.toLocaleString()}
            </span>
            {expandedSections.income ? <ChevronUp className="w-5 h-5 text-white/50" /> : <ChevronDown className="w-5 h-5 text-white/50" />}
          </div>
        </button>
        
        {expandedSections.income && (
          <div className="px-5 pb-5 border-t border-white/10">
            <div className="grid md:grid-cols-3 gap-4 mt-4">
              <div className="bg-white/5 rounded-xl p-4">
                <p className="text-white/60 text-sm">Total Settlements</p>
                <p className="text-xl font-bold text-white">{yearSummary.settlementCount}</p>
              </div>
              <div className="bg-white/5 rounded-xl p-4">
                <p className="text-white/60 text-sm">Total Miles</p>
                <p className="text-xl font-bold text-white">{yearSummary.totalMiles.toLocaleString()}</p>
              </div>
              <div className="bg-white/5 rounded-xl p-4">
                <p className="text-white/60 text-sm">Avg Per Settlement</p>
                <p className="text-xl font-bold text-white">
                  ${yearSummary.settlementCount > 0 
                    ? Math.round(yearSummary.grossIncome / yearSummary.settlementCount).toLocaleString()
                    : '0'}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Per Diem Section */}
      <div className="bg-white/5 rounded-2xl border border-white/10 overflow-hidden">
        <button
          onClick={() => toggleSection('perdiem')}
          className="w-full flex items-center justify-between p-5 hover:bg-white/5 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-cyan-500/20 rounded-lg">
              <Truck className="w-5 h-5 text-cyan-400" />
            </div>
            <div className="text-left">
              <h2 className="text-lg font-semibold text-white">Per Diem Deduction</h2>
              <p className="text-sm text-white/60">DOT Special Rate (80% deductible)</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-2xl font-bold text-cyan-400">
              ${yearSummary.perDiemDeduction.toLocaleString()}
            </span>
            {expandedSections.perdiem ? <ChevronUp className="w-5 h-5 text-white/50" /> : <ChevronDown className="w-5 h-5 text-white/50" />}
          </div>
        </button>
        
        {expandedSections.perdiem && (
          <div className="px-5 pb-5 border-t border-white/10">
            <div className="grid md:grid-cols-4 gap-4 mt-4">
              <div className="bg-white/5 rounded-xl p-4">
                <p className="text-white/60 text-sm">OTR Days</p>
                <p className="text-xl font-bold text-white">{yearSummary.otrDays}</p>
              </div>
              <div className="bg-white/5 rounded-xl p-4">
                <p className="text-white/60 text-sm">{selectedYear} Daily Rate</p>
                <p className="text-xl font-bold text-white">${PER_DIEM_RATES[selectedYear] || PER_DIEM_RATES[2025]}</p>
              </div>
              <div className="bg-white/5 rounded-xl p-4">
                <p className="text-white/60 text-sm">Total Eligible</p>
                <p className="text-xl font-bold text-white">${yearSummary.perDiemEligible.toLocaleString()}</p>
              </div>
              <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-xl p-4">
                <p className="text-cyan-400 text-sm font-medium">Deductible (80%)</p>
                <p className="text-xl font-bold text-cyan-400">${yearSummary.perDiemDeduction.toLocaleString()}</p>
              </div>
            </div>
            
            <div className="mt-4 p-4 bg-blue-500/10 border border-blue-500/30 rounded-xl text-sm">
              <p className="text-blue-300">
                <strong>💡 DOT Per Diem:</strong> Transportation workers can deduct per diem at 80% (vs. 50% for others). 
                This covers meals and incidental expenses while on the road. The {selectedYear} rate is ${PER_DIEM_RATES[selectedYear] || PER_DIEM_RATES[2025]}/day.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Business Expenses Section */}
      <div className="bg-white/5 rounded-2xl border border-white/10 overflow-hidden">
        <button
          onClick={() => toggleSection('expenses')}
          className="w-full flex items-center justify-between p-5 hover:bg-white/5 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-500/20 rounded-lg">
              <Receipt className="w-5 h-5 text-red-400" />
            </div>
            <div className="text-left">
              <h2 className="text-lg font-semibold text-white">Business Expenses</h2>
              <p className="text-sm text-white/60">Schedule C Deductions</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-2xl font-bold text-red-400">
              ${yearSummary.totalExpenses.toLocaleString()}
            </span>
            {expandedSections.expenses ? <ChevronUp className="w-5 h-5 text-white/50" /> : <ChevronDown className="w-5 h-5 text-white/50" />}
          </div>
        </button>
        
        {expandedSections.expenses && (
          <div className="px-5 pb-5 border-t border-white/10">
            <div className="space-y-3 mt-4">
              {Object.entries(DEDUCTIBLE_CATEGORIES).map(([key, cat]) => {
                const data = yearSummary.expenses[key];
                if (!data || data.amount === 0) return null;
                
                const percentage = (data.amount / yearSummary.totalExpenses) * 100;
                
                return (
                  <div key={key} className="bg-white/5 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-3">
                        <div className="p-1.5 bg-white/10 rounded-lg text-white/70">
                          {cat.icon}
                        </div>
                        <div>
                          <p className="text-white font-medium">{cat.label}</p>
                          <p className="text-white/50 text-xs">{cat.schedule}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold text-red-400">
                          ${data.amount.toLocaleString()}
                        </p>
                        <p className="text-white/50 text-xs">{data.count} entries</p>
                      </div>
                    </div>
                    <div className="w-full bg-white/10 rounded-full h-1.5">
                      <div
                        className="h-1.5 rounded-full bg-red-500"
                        style={{ width: `${Math.min(percentage, 100)}%` }}
                      />
                    </div>
                  </div>
                );
              })}
              
              {yearSummary.totalExpenses === 0 && (
                <div className="text-center py-8 text-white/50">
                  <Receipt className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No expense records for {selectedYear}</p>
                  <p className="text-sm mt-1">Import bank statements to add expenses</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Tax Summary Section */}
      <div className="bg-gradient-to-br from-purple-500/20 to-blue-500/20 rounded-2xl border border-purple-500/30 overflow-hidden">
        <button
          onClick={() => toggleSection('summary')}
          className="w-full flex items-center justify-between p-5 hover:bg-white/5 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-500/20 rounded-lg">
              <Calculator className="w-5 h-5 text-purple-400" />
            </div>
            <div className="text-left">
              <h2 className="text-lg font-semibold text-white">Tax Summary</h2>
              <p className="text-sm text-white/60">Schedule C Bottom Line</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-2xl font-bold text-purple-400">
              ${yearSummary.netProfit.toLocaleString()}
            </span>
            {expandedSections.summary ? <ChevronUp className="w-5 h-5 text-white/50" /> : <ChevronDown className="w-5 h-5 text-white/50" />}
          </div>
        </button>
        
        {expandedSections.summary && (
          <div className="px-5 pb-5 border-t border-white/10">
            <div className="mt-4 space-y-3">
              {/* Calculation breakdown */}
              <div className="font-mono text-sm space-y-2">
                <div className="flex justify-between text-white">
                  <span>Gross Income</span>
                  <span className="text-emerald-400">+ ${yearSummary.grossIncome.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-white/80">
                  <span>Business Expenses</span>
                  <span className="text-red-400">- ${yearSummary.totalExpenses.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-white/80">
                  <span>Per Diem Deduction (80%)</span>
                  <span className="text-cyan-400">- ${yearSummary.perDiemDeduction.toLocaleString()}</span>
                </div>
                <div className="border-t border-white/20 pt-2 flex justify-between font-bold text-lg">
                  <span className="text-white">Net Profit (Line 31)</span>
                  <span className={yearSummary.netProfit >= 0 ? 'text-purple-400' : 'text-orange-400'}>
                    ${yearSummary.netProfit.toLocaleString()}
                  </span>
                </div>
              </div>
              
              {/* Tax estimates */}
              <div className="mt-6 p-4 bg-white/5 rounded-xl">
                <h3 className="text-white font-medium mb-3 flex items-center gap-2">
                  <Building className="w-4 h-4 text-purple-400" />
                  Estimated Tax Liability
                </h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-white/70">Self-Employment Tax (15.3%)</span>
                    <span className="text-white">
                      ~${Math.round(yearSummary.netProfit > 0 ? yearSummary.netProfit * 0.9235 * 0.153 : 0).toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-white/70">Est. Income Tax (~22%)</span>
                    <span className="text-white">
                      ~${Math.round(yearSummary.netProfit > 0 ? yearSummary.netProfit * 0.22 : 0).toLocaleString()}
                    </span>
                  </div>
                  <div className="border-t border-white/10 pt-2 flex justify-between font-medium">
                    <span className="text-white">Total Estimated</span>
                    <span className="text-purple-400">
                      ~${Math.round(yearSummary.estimatedTax).toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>
              
              {/* Effective rate */}
              {yearSummary.grossIncome > 0 && (
                <div className="text-center p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-xl">
                  <p className="text-emerald-400 font-medium">
                    Effective Tax Rate: {((yearSummary.estimatedTax / yearSummary.grossIncome) * 100).toFixed(1)}%
                  </p>
                  <p className="text-white/60 text-xs mt-1">
                    Tax savings from deductions: ~${Math.round((yearSummary.totalExpenses + yearSummary.perDiemDeduction) * 0.35).toLocaleString()}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* All Years Quick View */}
      <div className="bg-white/5 rounded-2xl p-6 border border-white/10">
        <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Calendar className="w-5 h-5 text-shuttle-sand" />
          All Years Overview
        </h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left py-2 px-3 text-white/70">Year</th>
                <th className="text-right py-2 px-3 text-white/70">Income</th>
                <th className="text-right py-2 px-3 text-white/70">Expenses</th>
                <th className="text-right py-2 px-3 text-white/70">Per Diem</th>
                <th className="text-right py-2 px-3 text-white/70">Net</th>
                <th className="text-center py-2 px-3 text-white/70">Data</th>
              </tr>
            </thead>
            <tbody>
              {availableYears.map(year => {
                const yearData = expenses.filter(e => e.year === year);
                const settlements = yearData.filter(e => e.category === 'settlements');
                const income = settlements.reduce((s, e) => s + (e.amount || 0), 0);
                const expTotal = yearData.filter(e => e.category !== 'settlements').reduce((s, e) => s + (e.amount || 0), 0);
                const otrDays = settlements.reduce((sum, s) => {
                  const miles = s.miles || Math.round((s.amount || 0) / 1.75);
                  return sum + Math.max(1, Math.ceil(miles / 500));
                }, 0);
                const perDiem = otrDays * (PER_DIEM_RATES[year] || 66) * 0.8;
                const net = income - expTotal - perDiem;
                const monthsWithData = new Set(yearData.map(e => e.month)).size;
                
                return (
                  <tr 
                    key={year} 
                    className={`border-b border-white/5 hover:bg-white/5 cursor-pointer ${year === selectedYear ? 'bg-white/10' : ''}`}
                    onClick={() => setSelectedYear(year)}
                  >
                    <td className="py-2 px-3 text-white font-medium">{year}</td>
                    <td className="py-2 px-3 text-right text-emerald-400">${income.toLocaleString()}</td>
                    <td className="py-2 px-3 text-right text-red-400">${expTotal.toLocaleString()}</td>
                    <td className="py-2 px-3 text-right text-cyan-400">${Math.round(perDiem).toLocaleString()}</td>
                    <td className={`py-2 px-3 text-right font-medium ${net >= 0 ? 'text-purple-400' : 'text-orange-400'}`}>
                      ${Math.round(net).toLocaleString()}
                    </td>
                    <td className="py-2 px-3 text-center">
                      <span className={`px-2 py-0.5 rounded text-xs ${
                        monthsWithData >= 10 ? 'bg-emerald-500/20 text-emerald-400' :
                        monthsWithData >= 6 ? 'bg-yellow-500/20 text-yellow-400' :
                        monthsWithData > 0 ? 'bg-orange-500/20 text-orange-400' :
                        'bg-white/10 text-white/40'
                      }`}>
                        {monthsWithData}/12
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Disclaimer */}
      <div className="text-center text-white/40 text-xs p-4">
        <p>⚠️ This is an estimate only. Consult a tax professional for accurate filing.</p>
        <p>Per diem rates based on IRS/DOT guidelines. Your situation may vary.</p>
      </div>
    </div>
  );
}










